import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { UrlFormatter } from 'shared';
import { ParseService } from './parse.service';

/**
 * Native-Angular port of the AngularJS `Layers` service
 * (account-v1/src/core/api/layers.js). Verbatim: relative `/api/...` URLs built
 * with the native `UrlFormatter.format` (byte-identical to the v1 `urlFormatter`),
 * the same `prestay` `setupCampaign` post-processing (incl. the raw-value
 * `_isActiveOrder = -isActive` BEFORE the int-parse, and the `parse.intOrDefault`
 * field coercions), and Promise-returning methods so the reused redux-thunks
 * (`account-v2/.../actions/propertyApi.ts`, `…/journeys/actions`) call it
 * unchanged via the facade extra-args. `firstValueFrom(http.get(url))` yields the
 * response BODY — the same value the v1 `$http.get(url).then(r => r.data)` resolved.
 *
 * WS5 #10 Stage A: replaces the `$injector.get('Layers')` bridge in
 * the advocacy NgRx facade (the gate-covered data-load path). `providedIn:'root'`
 * so consumers `inject()` it directly (no barrel/module wiring).
 */
@Injectable({ providedIn: 'root' })
export class LayersApiService {
  private readonly http = inject(HttpClient);
  private readonly parse = inject(ParseService);

  private readonly basePath = '/api/:propertyId/properties/:propertyId/layers/:layer';

  prestay(params: any): Promise<any> {
    params.layer = 'prestay';
    return firstValueFrom(this.http.get<any>(UrlFormatter.format(this.basePath, params, true))).then((data) => {
      if (data && data.campaigns) {
        ngForEach(data.campaigns.item, (c: any) => this.setupCampaign(c));
      }
      return data;
    });
  }

  quotelist(params: any): Promise<any> {
    params.layer = 'quotelist';
    return firstValueFrom(this.http.get<any>(UrlFormatter.format(this.basePath, params, true)));
  }

  poststay(params: any): Promise<any> {
    params.layer = 'poststay';
    return firstValueFrom(this.http.get<any>(UrlFormatter.format(this.basePath, params, true)));
  }

  photocontest(params: any): Promise<any> {
    params.layer = 'photocontest';
    return firstValueFrom(this.http.get<any>(UrlFormatter.format(this.basePath, params, true)));
  }

  homepage(params: any): Promise<any> {
    params.layer = 'homepage';
    return firstValueFrom(this.http.get<any>(UrlFormatter.format(this.basePath, params, true)));
  }

  groupAdvocacy(propertyUUID: string): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(UrlFormatter.format(this.basePath, { layer: 'groupadvocacy', propertyId: propertyUUID })),
    );
  }

  create(params: any, model: any): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(UrlFormatter.format('/api/:companyId/properties/layers/:propertyId/create', params), model),
    );
  }

  private setupCampaign(campaign: any): void {
    campaign._isActiveOrder = -campaign.isActive;
    if (campaign.goals) {
      ngForEach(campaign.goals.item, (g: any) => this.setupGoal(g));
    }
    this.batchConvert(campaign, ['type', 'isActive', 'campaignGuests', 'campaignShares', 'campaignUniqueVisitors']);
  }

  private setupGoal(goal: any): void {
    this.batchConvert(goal, ['clicks', 'signups', 'type', 'isActive']);
  }

  private batchConvert(object: any, properties: string[]): void {
    properties.forEach((property) => {
      object[property] = this.parse.intOrDefault(object[property]);
    });
  }
}

/**
 * Replicates `angular.forEach` for the `.item` collections, which the flip.to API
 * returns as an ARRAY (many) OR a single OBJECT (one): array → iterate elements,
 * object → iterate own-property values, null/undefined → no-op. A native `.forEach`
 * would throw on the single-object form, so this preserves the v1 behaviour exactly.
 */
function ngForEach(collection: any, fn: (value: any) => void): void {
  if (collection == null) return;
  if (Array.isArray(collection)) {
    collection.forEach((v) => fn(v));
  } else if (typeof collection === 'object') {
    Object.keys(collection).forEach((k) => fn(collection[k]));
  }
}
